import { Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack } from "@mui/material";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { Button } from "@src/appV2/redesign/components/Button";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { PageWrapper } from "@src/appV2/redesign/components/PageWrapper";
import { CreditProgressBarWithTooltip } from "@src/appV2/redesign/EmergencyCredits/components/CreditProgressBarWithTooltip";
import { useCallback, useMemo } from "react";

import { ScoreDisplay } from "./components/ScoreDisplay";
import { ScoreLineItemsContainer } from "./components/ScoreLineItemsContainer";
import { GRADIENTS, LAYOUT } from "./constants";
import { useScoreAnimationSequence } from "./hooks/useScoreAnimationSequence";
import { type YourClipboardScorePageProps } from "./types";

export function YourClipboardScorePage(props: YourClipboardScorePageProps) {
  const {
    startScore,
    scoreChanges,
    subtitle,
    gradientVariant,
    showCreditProgressBar,
    creditProgressBarProps,
    hasLatenessCredit,
    onApplyCredit,
    onContinue,
    isButtonDisabled = false,
  } = props;

  const { currentEndScore, isScoreAnimating, visibleLineItemCount, isButtonEnabled } =
    useScoreAnimationSequence(startScore, scoreChanges);

  // Calculate starting score for animation
  // When animating to a new score, we need to know where we were animating from
  const previousEndScore = useMemo(() => {
    if (visibleLineItemCount === 0) {
      return startScore;
    }
    return scoreChanges
      .slice(0, visibleLineItemCount)
      .reduce((sum, change) => sum + change.value, startScore);
  }, [startScore, scoreChanges, visibleLineItemCount]);

  useLogEffect(APP_V2_USER_EVENTS.YOUR_CLIPBOARD_SCORE_PAGE_VIEWED, {
    startScore,
    scoreChanges,
    gradientVariant,
    hasLatenessCredit,
  });

  const handleButtonClick = useCallback((): void => {
    if (hasLatenessCredit) {
      logEvent(APP_V2_USER_EVENTS.YOUR_CLIPBOARD_SCORE_APPLY_CREDIT_CLICKED, {
        startScore,
        finalScore: currentEndScore,
      });
      onApplyCredit();
    } else {
      logEvent(APP_V2_USER_EVENTS.YOUR_CLIPBOARD_SCORE_CONTINUE_CLICKED, {
        startScore,
        finalScore: currentEndScore,
      });
      onContinue();
    }
  }, [hasLatenessCredit, onApplyCredit, onContinue, startScore, currentEndScore]);

  const buttonText = hasLatenessCredit ? "Apply my lateness credit" : "Continue";
  const buttonEnabled = isButtonEnabled && !isButtonDisabled;

  return (
    <PageWrapper
      sx={{
        background: GRADIENTS[gradientVariant],
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
      }}
    >
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Stack
          spacing={9}
          sx={{
            paddingTop: 10,
            paddingX: 5,
            textAlign: "center",
            alignItems: "center",
          }}
        >
          <Stack spacing={4} alignItems="center">
            <Text
              variant="h3"
              textAlign="center"
              sx={(theme) => ({
                paddingX: 6,
                color: theme.palette.text.secondary,
              })}
            >
              Your Clipboard Score
            </Text>

            <Text
              semibold
              variant="h6"
              textAlign="center"
              sx={(theme) => ({
                paddingX: 6,
                color: theme.palette.text.secondary,
              })}
            >
              {subtitle}
            </Text>
          </Stack>

          <Stack spacing={7} alignItems="center">
            <ScoreDisplay
              startScore={previousEndScore}
              currentEndScore={currentEndScore}
              isAnimating={isScoreAnimating}
            />

            {showCreditProgressBar && isDefined(creditProgressBarProps) && (
              <Stack spacing={1} alignItems="center" sx={{ width: LAYOUT.CONTENT_MAX_WIDTH }}>
                <CreditProgressBarWithTooltip {...creditProgressBarProps} showTooltip={false} />
                {creditProgressBarProps.currentCount < creditProgressBarProps.totalCount && (
                  <Text
                    variant="caption"
                    textAlign="center"
                    sx={(theme) => ({ color: theme.palette.text.tertiary })}
                  >
                    {creditProgressBarProps.currentCount}/{creditProgressBarProps.totalCount} shifts
                    complete
                  </Text>
                )}
              </Stack>
            )}
          </Stack>

          <ScoreLineItemsContainer
            startScore={startScore}
            scoreChanges={scoreChanges}
            visibleLineItemCount={visibleLineItemCount}
          />
        </Stack>
      </Box>

      <DialogFooter orientation="horizontal">
        <Button
          fullWidth
          variant="contained"
          size="large"
          disabled={!buttonEnabled}
          data-testid="your-clipboard-score-action-button"
          sx={{
            visibility: buttonEnabled ? "visible" : "hidden",
          }}
          onClick={handleButtonClick}
        >
          {buttonText}
        </Button>
      </DialogFooter>
    </PageWrapper>
  );
}
